import type { DirectoryUser } from '@halo/shared';

/**
 * BỘ LỌC XEM: thu hẹp phần ĐANG HIỂN THỊ, KHÔNG phải phân quyền.
 *
 * «Có quyền xem không» do máy chủ quyết (nó chỉ gửi về bản ghi trong phạm vi). Bộ lọc này trả
 * lời câu khác hẳn: «có đang muốn nhìn không». Hai thứ phải tách bạch — mọi chốt chặn thao tác
 * vẫn dựa vào quyền, nếu không thì lọc đi một người là app báo «không đủ quyền» sai bét.
 *
 * Bốn tầng độc lập, ô nào trống là không lọc tầng đó. Ô cụ thể hơn thắng:
 * người > nhóm > phòng > chi nhánh.
 */
export interface ViewFilter {
  /** Mã chi nhánh (xem `domain/branches.ts`); '' = mọi chi nhánh. */
  branchId: string;
  departmentId: string;
  teamId: string;
  username: string;
}

/** Những gì cần để trả lời «bản ghi này của ai» ngoài chính tên đăng nhập. */
export interface ViewFilterContext {
  userByUsername: (username: string) => DirectoryUser | undefined;
  /** Mã các phòng của một chi nhánh. */
  departmentsOfBranch: (branchId: string) => ReadonlySet<string>;
}

export const EMPTY_VIEW_FILTER: ViewFilter = {
  branchId: '',
  departmentId: '',
  teamId: '',
  username: '',
};

export function isFiltering(filter: ViewFilter): boolean {
  return Boolean(filter.username || filter.teamId || filter.departmentId || filter.branchId);
}

/** Mô tả phần đang xem bằng lời, để chú thích biểu đồ/bảng nói rõ số liệu là của ai. */
export function describeViewFilter(
  filter: ViewFilter,
  names: {
    displayName: (username: string) => string;
    unitName: (unitId: string) => string;
    branchName?: (branchId: string) => string;
  },
): string {
  if (filter.username) return names.displayName(filter.username);
  if (filter.teamId) return `nhóm ${names.unitName(filter.teamId)}`;
  if (filter.departmentId) return `phòng ${names.unitName(filter.departmentId)}`;
  if (filter.branchId) return names.branchName?.(filter.branchId) ?? filter.branchId;
  return 'toàn bộ phạm vi';
}

export function passesViewFilter(
  filter: ViewFilter,
  owner: string | null,
  context: ViewFilterContext,
): boolean {
  if (!isFiltering(filter)) return true;
  if (!owner) return true; // bản ghi dùng chung luôn hiện
  if (filter.username) return owner === filter.username;
  const user = context.userByUsername(owner);
  if (!user) return false;
  if (filter.teamId) return user.teamId === filter.teamId;
  if (filter.departmentId) return user.departmentId === filter.departmentId;
  return context.departmentsOfBranch(filter.branchId).has(user.departmentId);
}

/** Người chọn được ở ô «người»: thu hẹp theo chi nhánh/phòng/nhóm đang chọn. */
export function selectableUsers(
  filter: Pick<ViewFilter, 'branchId' | 'departmentId' | 'teamId'>,
  users: readonly DirectoryUser[],
  departmentsOfBranch: (branchId: string) => ReadonlySet<string> = () => new Set(),
): DirectoryUser[] {
  const inBranch = filter.branchId ? departmentsOfBranch(filter.branchId) : null;
  return users.filter(
    (user) =>
      (!filter.teamId || user.teamId === filter.teamId) &&
      (!filter.departmentId || user.departmentId === filter.departmentId) &&
      (!inBranch || inBranch.has(user.departmentId)),
  );
}

/** Đổi một tầng thì xoá các tầng CỤ THỂ HƠN không còn khớp — tránh tổ hợp lọc ra rỗng khó hiểu. */
export function changeViewFilter(
  current: ViewFilter,
  change: Partial<ViewFilter>,
  context: {
    teamDepartment: (teamId: string) => string | null;
    users: readonly DirectoryUser[];
    departmentsOfBranch?: (branchId: string) => ReadonlySet<string>;
  },
): ViewFilter {
  const next = { ...current, ...change };
  const departmentsOfBranch = context.departmentsOfBranch ?? (() => new Set<string>());
  // Đổi chi nhánh thì phòng đang chọn phải thuộc chi nhánh mới, không thì bỏ chọn phòng (và nhóm).
  if ('branchId' in change && next.branchId && next.departmentId) {
    if (!departmentsOfBranch(next.branchId).has(next.departmentId)) {
      next.departmentId = '';
      next.teamId = '';
    }
  }
  if ('departmentId' in change && next.teamId) {
    const parent = context.teamDepartment(next.teamId);
    if (next.departmentId && parent !== next.departmentId) next.teamId = '';
  }
  if (
    next.username &&
    !selectableUsers(next, context.users, departmentsOfBranch).some(
      (u) => u.username === next.username,
    )
  ) {
    next.username = '';
  }
  return next;
}
